import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Grid } from '@wds/grid';
import { IconInfo, IconKey, IconMobile } from '@wds/icons';
import { Popout } from '@wds/popout';
import { Switch } from '@wds/switch';
import { Typography } from '@wds/typography';
import { Tooltip } from '@wds/tooltip';

import PropTypes from 'prop-types';

import STRINGS from '../../../constants';
import CardTemplate from '../../../../sma.common/components/react/components/CardTemplate';
import ImageUpload from '../../../../sma.common/components/react/components/imageUpload/imageUpload';
import EditableTextField from '../../../../sma.common/components/react/components/EditableTextField';
import EditableSelectField from '../../../../sma.common/components/react/components/EditableSelectField';
import DeactivateMFAModal from '../../modals/DeactivateMFAModal';
import GenerateCodesModal from '../../modals/GenerateCodesModal';
import MFAEnrollmentModal from '../../modals/MFAEnrollmentModal';
import ChangeMFADeviceModal from '../../modals/ChangeMFADeviceModal';
import ChangeRecoveryEmailModal from '../../modals/ChangeRecoveryEmailModal';

const tabStrings = STRINGS.accountSettingsPage.myProfileTab;

const getCookie = (name) => {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) {
		return parts.pop().split(';').shift();
	}
	return '';
};

const methodMap = {
	0: 'totp',
	1: 'email',
};

const MyProfileTab = (props) => {
	const {
		settings,
		setSettings,
		languages,
		timezones,
		cancelEmailChangeRequest,
		resendEmailChangeRequest,
		handleDisconnectGoogleSignOn,
		reset,
		editingFields,
		setEditingFields,
		assumedRole,
		usesSso,
		usesGoogleSSO,
		setToastInfo,
		isSuperuser,
	} = props;

	const [mfaEnabledCheckbox, setMfaEnabledCheckbox] = useState(settings?.mfa_enabled || false);
	const [showDeactivateMfaModal, setShowDeactivateMfaModal] = useState(false);
	const [showMFAEnrollmentModal, setShowMFAEnrollmentModal] = useState(false);
	const [showGenerateCodesModal, setShowGenerateCodesModal] = useState(false);
	const [showChangeMFADeviceModal, setShowChangeMFADeviceModal] = useState(false);
	const [showChangeRecoveryEmailModal, setShowChangeRecoveryEmailModal] = useState(false);
	const [recoveryEmail, setRecoveryEmail] = useState(null);
	const [loadingRecoveryEmail, setLoadingRecoveryEmail] = useState(false);
	const enrollmentFullyCompletedRef = useRef(false);

	useEffect(() => {
		setMfaEnabledCheckbox(settings?.mfa_enabled);
	}, [settings]);

	useEffect(() => {
		if (settings?.mfa_enabled) {
			fetchRecoveryEmail();
		}
	}, [settings?.mfa_enabled]);

	useEffect(() => {
		if (!showDeactivateMfaModal) {
			setMfaEnabledCheckbox(Boolean(settings?.mfa_enabled));
		}
	}, [showDeactivateMfaModal, settings?.mfa_enabled]);

	useEffect(() => {
		// If the stored language_preference is no longer one of the site's
		// languages (e.g. an admin narrowed extra_languages after this user set
		// their preference), normalise settings.language_preference to a language
		// the site actually supports. Without this, the language <Select> displays
		// a fallback value but the underlying settings state still holds the
		// unsupported one, so any Save (even for an unrelated field, since the
		// whole settings object is submitted in one PATCH) is rejected with 400
		// and every co-submitted change is silently discarded.
		if (
			settings?.language_preference
			&& languages.site_languages
			&& !languages.site_languages[settings.language_preference]
		) {
			setSettings((prev) => ({
				...prev,
				language_preference: Object.keys(languages.site_languages)[0],
			}));
		}
	}, [settings?.language_preference, languages.site_languages]);

	const fetchRecoveryEmail = async () => {
		setLoadingRecoveryEmail(true);
		try {
			const response = await fetch(reverse('api-internal-mfa-recovery-email'), {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
			});
			const data = await response.json();
			console.log('Recovery email response:', data);
			if (data.has_recovery_email && data.is_verified) {
				setRecoveryEmail({
					maskedEmail: data.email || data.masked_email,
					isVerified: data.is_verified,
				});
			}
		} catch (err) {
			console.error('Error fetching recovery email:', err);
		} finally {
			setLoadingRecoveryEmail(false);
		}
	};

	const syncMfaStateFromServer = async () => {
		try {
			const response = await fetch(reverse('api-internal-mfa-enroll'), {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
			});
			if (!response.ok) return;

			const data = await response.json();
			const enrolledMethods = data?.enrolled_methods || [];
			const enabled = enrolledMethods.length > 0;

			setSettings((prev) => ({
				...prev,
				mfa_enabled: enabled,
				mfa_methods: enrolledMethods,
			}));
			setMfaEnabledCheckbox(enabled);
		} catch (err) {
			// Best-effort sync; keep current UI state if request fails.
		}
	};

	const handleMfaEnabledChange = (e) => {
		setMfaEnabledCheckbox(e.target.checked);
		if (e.target.checked) {
			setShowMFAEnrollmentModal(true);
		} else {
			setShowDeactivateMfaModal(true);
		}
	};

	const handleCloseMFAModal = async () => {
		if (!enrollmentFullyCompletedRef.current) {
			try {
				await fetch(reverse('api-internal-mfa-enroll'), {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRFToken': getCookie('csrftoken'),
					},
					body: JSON.stringify({ action: 'cancel_enrollment' }),
				});
			} catch (e) {
				// Best-effort cleanup
			}
		}

		await syncMfaStateFromServer();
		enrollmentFullyCompletedRef.current = false;
		setShowMFAEnrollmentModal(false);
		setShowChangeMFADeviceModal(false);
	};

	const handleCloseChangeMFADeviceModal = (newMethod) => {
		if (newMethod) {
			setSettings((prevSettings) => ({
				...prevSettings,
				mfa_enabled: true,
				mfa_methods: [{ method: newMethod === 'totp' ? 0 : 1 }],
			}));
		}

		// Clear transient MFA change-flow state so reopening the modal always
		// initializes from fresh backend state and does not flicker to stale pages.
		setSettings((prevSettings) => {
			const nextSettings = { ...prevSettings };
			delete nextSettings.challengeCode;
			delete nextSettings.new_method;
			delete nextSettings.method_id;
			delete nextSettings.status;
			delete nextSettings.changeInProgress;
			delete nextSettings.qrCodeString;
			delete nextSettings.securityKeyString;
			delete nextSettings.cooldown_remaining;
			delete nextSettings.needsRecoveryEmail;
			delete nextSettings.recovery_codes;
			return nextSettings;
		});

		setShowMFAEnrollmentModal(false);
		setShowChangeMFADeviceModal(false);
	};

	const handleDeactivateMFA = () => {
		setShowDeactivateMfaModal(false);
		setSettings((prevSettings) => ({
			...prevSettings,
			mfa_enabled: false,
			mfa_methods: [],
		}));
		setToastInfo({
			show: true,
			color: 'success',
			title: STRINGS.toast.success,
			text: STRINGS.toast.mfaRemoved,
		});
	};

	const handleRecoveryEmailSuccess = (newEmail) => {
		setRecoveryEmail({
			maskedEmail: newEmail,
			isVerified: true,
		});
		setToastInfo({
			show: true,
			color: 'success',
			title: STRINGS.toast.success,
			text: STRINGS.toast.recoveryEmailUpdated || 'Recovery email has been updated',
		});
	};

	const handleRecoveryEmailVerifiedFromEnrollment = (recoveryEmailAddress) => {
		if (!recoveryEmailAddress) return;
		enrollmentFullyCompletedRef.current = true;
		setRecoveryEmail({
			maskedEmail: recoveryEmailAddress,
			isVerified: true,
		});
	};

	return (
		<>
			<CardTemplate
				cardTitle={tabStrings.profileImage}
				id='profile-image'
			>
				<Box id='profile-image'>
					<Box px={3}>
						<ImageUpload
							label={tabStrings.profileImage}
							id='profile-image'
							acceptTypes='image/png;base64'
							url='/internal/user/image/'
						/>
					</Box>
				</Box>
			</CardTemplate>
			<CardTemplate cardTitle={tabStrings.personalInfo} id='personal-info'>
				{/* Name */}
				<Grid noBleeds>
					<Grid.Item xs={12}>
						{Object.keys(settings).length > 0 && (
							<EditableTextField
								id='name'
								label={STRINGS.name}
								fields={[
									{
										id: 'first_name',
										label: STRINGS.firstName,
										type: 'text',
										isRequired: true,
										errorMessage: STRINGS.fieldErrors.text,
										value: settings?.first_name,
									},
									{
										id: 'last_name',
										label: STRINGS.lastName,
										type: 'text',
										isRequired: true,
										errorMessage: STRINGS.fieldErrors.text,
										value: settings?.last_name,
									},
								]}
								editable={(!usesSso && !usesGoogleSSO)}
								state={settings}
								setState={setSettings}
								reset={reset}
								editingFields={editingFields}
								setEditingFields={setEditingFields}
							/>
						)}
					</Grid.Item>
				</Grid>

				{/* Email and Recovery Email */}
				<Grid noBleeds mt={4}>
					<Grid.Item xs={12} md={6}>
						{Object.keys(settings).length > 0 && (
							<>
								<EditableTextField
									id='email'
									label={STRINGS.email}
									fields={[
										{
											id: 'email',
											label: STRINGS.email,
											type: 'email',
											isRequired: true,
											errorMessage: STRINGS.fieldErrors.email,
											value: settings?.email,
										},
									]}
									editable={(!usesSso && !usesGoogleSSO && !settings?.temp_email)}
									state={settings}
									setState={setSettings}
									reset={reset}
									editingFields={editingFields}
									setEditingFields={setEditingFields}
								/>
								{settings.temp_email && (
									<Box mt={1} style={{ maxWidth: 350 }}>
										<Popout
											size='lg'
											trigger={(
												<Box display='flex' gap={2}>
													<IconInfo
														size='sm'
														title='info'
														color='darkMuted'
													/>
													<Typography
														variant='caption'
														weight='medium'
														lineHeight='1'
													>
														{STRINGS.emailAddressChangePending}
													</Typography>
												</Box>
											)}
										>
											<Popout.Body>
												{tabStrings.emailChangeRequested}
												{' '}
												{settings.temp_email}
												{'. '}
												{tabStrings.emailChangeCheckEmail}
											</Popout.Body>
											<Popout.Footer align='left'>
												<Button
													size='sm'
													color='warning'
													onClick={() => cancelEmailChangeRequest()}
												>
													{STRINGS.cancel}
												</Button>
												<Button
													size='sm'
													color='alt'
													onClick={() => resendEmailChangeRequest()}
												>
													{STRINGS.resendRequest}
												</Button>
											</Popout.Footer>
										</Popout>
									</Box>
								)}
							</>
						)}
					</Grid.Item>
					<Grid.Item xs={12} md={6}>
						{settings?.mfa_enabled && !assumedRole && recoveryEmail?.maskedEmail && (
							<Box display='flex' flexDirection='column' gap={2}>
								<Box display='flex' gap={2}>
									<Typography variant='bodySm' weight='medium'>
										{STRINGS.recoveryEmail || 'Recovery Email'}
									</Typography>
									<Typography
										variant='bodySm'
										weight='medium'
										color='link'
										component='a'
										href='#'
										style={{ cursor: 'pointer' }}
										onClick={(e) => {
											e.preventDefault();
											setShowChangeRecoveryEmailModal(true);
										}}
									>
										{STRINGS.change || 'Change'}
									</Typography>
								</Box>
								<Typography variant='bodySm'>
									{recoveryEmail.maskedEmail}
								</Typography>
							</Box>
						)}
					</Grid.Item>
				</Grid>

				{/* Password */}
				<Grid noBleeds mt={4}>
					<Grid.Item xs={12}>
						{!assumedRole && !usesGoogleSSO && Object.keys(settings).length > 0 && (
							<EditableTextField
								id='password'
								label={STRINGS.password}
								fields={[
									{
										id: 'current_password',
										label: STRINGS.currentPassword,
										type: 'password',
										errorMessage: STRINGS.fieldErrors.currentPassword,
										value: settings?.current_password || '',
										isRequired: true,
									},
									{
										id: 'new_password',
										label: STRINGS.newPassword,
										type: 'password',
										errorMessage: STRINGS.fieldErrors.newPassword,
										value: settings?.new_password || '',
									},
								]}
								state={settings}
								setState={setSettings}
								reset={reset}
								editingFields={editingFields}
								setEditingFields={setEditingFields}
							/>
						)}
					</Grid.Item>
				</Grid>

				{/* Divider */}
				<Box mt={4} mb={4} style={{ borderTop: '1px solid #E5E7EB' }} />

				{/* MFA Section */}
				{!assumedRole && !usesSso && !usesGoogleSSO && !isSuperuser && (
					<Grid noBleeds>
						<Grid.Item xs={12} md={settings?.mfa_enabled ? 6 : 12}>
							<Box>
								<Box display='flex' gap={2} alignItems='center' mb={1}>
									<Typography
										weight='medium'
										id='mfa-switch-label'
									>
										{tabStrings.mfaEnabled}
									</Typography>
									{!settings.can_disable_mfa ? (
										<Tooltip text={STRINGS.site_requires_mfa}>
											<Switch
												checked={mfaEnabledCheckbox}
												id='mfa-enabled'
												aria-labelledby='mfa-switch-label'
												aria-label={STRINGS.labels.mfaSwitch}
												disabled={!settings.can_disable_mfa}
												aria-disabled={!settings.can_disable_mfa}
											/>
										</Tooltip>
									) : (
										<Switch
											checked={mfaEnabledCheckbox}
											onChange={(e) => handleMfaEnabledChange(e)}
											id='mfa-enabled'
											aria-labelledby='mfa-switch-label'
											aria-label={STRINGS.labels.mfaSwitch}
											disabled={!settings.can_disable_mfa}
											aria-disabled={!settings.can_disable_mfa}
										/>
									)}
								</Box>
								{settings.mfa_enabled ? (
									settings?.mfa_methods && Object.entries(settings?.mfa_methods[0]).length > 0 && (
										<Box display='flex' gap={2}>
											<Typography weight='medium'>{tabStrings.enrolledMethod}:</Typography>
											<Typography>
												{tabStrings?.[methodMap[settings?.mfa_methods[0].method]]}
											</Typography>
										</Box>
									)
								) : (
									<Box display='flex' gap={2}>
										<Typography>
											{tabStrings.mfaLearnMoreText}
										</Typography>
										<Typography
											component='a'
											href={window.LOCALE?.mfaLearnMore}
											target='_blank'
											rel='noopener noreferrer'
											variant='link'
										>
											{tabStrings.mfaLearnMoreLink}
										</Typography>
									</Box>
								)}
							</Box>
						</Grid.Item>
						{settings?.mfa_enabled ? (
							<Grid.Item xs={12} md={6}>
								<Button
									color='alt'
									id='change-device'
									onClick={() => setShowChangeMFADeviceModal(true)}
								>
									<IconMobile size='sm' title='mobile_icon' />
									{tabStrings.changeEnrolledDevice}
								</Button>
							</Grid.Item>
						) : null}
					</Grid>
				)}
			</CardTemplate>
			{(!assumedRole || assumedRole.super) && settings?.social && Object.keys(settings?.social).length > 0 && (
				<CardTemplate
					cardTitle={tabStrings.linkedAccounts}
					id='linked-accounts'
				>
					<Box id='linked-accounts-id'>
						<Grid noBleeds xs={16}>
							<Grid.Item xs={12}>
								<Box>
									<Box>
										<Typography
											variant='bodySm'
											weight='medium'
										>
											{tabStrings.socialSignOn}
										</Typography>
									</Box>
									<Box display='flex' justifyContent='space-between'>
										<Typography
											lineHeight={2.5}
											color='link'
										>
											{STRINGS.googleSSO}
											{' '}
											(
											{settings.social.id}
											)
										</Typography>
										<Button
											color='warning'
											onClick={() => handleDisconnectGoogleSignOn()}
											id='disable-google'
										>
											{tabStrings.disconnectAccount}
										</Button>
									</Box>
								</Box>
							</Grid.Item>

						</Grid>
					</Box>
				</CardTemplate>
			)}
			<CardTemplate
				cardTitle={tabStrings.generalPreferences}
				id='general-preferences'
			>
				<Grid noBleeds>
					<Grid.Item>
						<Grid noBleeds>
							<Grid.Item>
								<Box>
									{Object.keys(languages).length > 0 && Object.keys(settings).length > 0 && (
										<EditableSelectField
											id='language'
											label={STRINGS.preferredLanguage}
											fields={[
												{
													id: 'language_preference',
													label: STRINGS.preferredLanguage,
													type: 'select',
													isRequired: true,
													errorMessage: STRINGS.fieldErrors.language,
													defaultValue: languages.site_languages?.[
														settings?.language_preference
													]
														? settings?.language_preference
														: Object.keys(
															languages.site_languages || languages.all_languages || {},
														)[0],
													options: languages.site_languages || languages.all_languages,
												},
											]}
											state={settings}
											setState={setSettings}
											reset={reset}
											editingFields={editingFields}
											setEditingFields={setEditingFields}
										/>
									)}
								</Box>
							</Grid.Item>
							<Grid.Item>
								<Box>
									{Object.keys(timezones).length > 0 && Object.keys(settings).length > 0 && (
										<EditableSelectField
											id='timezone'
											label={STRINGS.timezone}
											fields={[
												{
													id: 'timezone',
													label: STRINGS.timezone,
													type: 'select',
													isRequired: true,
													errorMessage: STRINGS.fieldErrors.timezone,
													defaultValue: settings?.timezone,
													options: timezones,
												},
											]}
											state={settings}
											setState={setSettings}
											reset={reset}
											editingFields={editingFields}
											setEditingFields={setEditingFields}
										/>
									)}
								</Box>
							</Grid.Item>
						</Grid>
					</Grid.Item>
				</Grid>
			</CardTemplate>
			{showMFAEnrollmentModal && (
				<MFAEnrollmentModal
					open={showMFAEnrollmentModal}
					context={settings}
					setContext={setSettings}
					handleClose={handleCloseMFAModal}
					onRecoveryEmailVerified={handleRecoveryEmailVerifiedFromEnrollment}
				/>
			)}
			{showGenerateCodesModal && (
				<GenerateCodesModal
					open={showGenerateCodesModal}
					setOpen={setShowGenerateCodesModal}
				/>
			)}
			{showDeactivateMfaModal && (
				<DeactivateMFAModal
					open={showDeactivateMfaModal}
					setOpen={setShowDeactivateMfaModal}
					handleOnSuccess={handleDeactivateMFA}
					method={methodMap[settings?.mfa_methods[0].method]}
				/>
			)}
			{showChangeMFADeviceModal && (
				<ChangeMFADeviceModal
					open={showChangeMFADeviceModal}
					context={settings}
					setContext={setSettings}
					handleClose={handleCloseChangeMFADeviceModal}
				/>
			)}
			{showChangeRecoveryEmailModal && (
				<ChangeRecoveryEmailModal
					open={showChangeRecoveryEmailModal}
					handleClose={() => {
						console.log('Closing recovery email modal');
						setShowChangeRecoveryEmailModal(false);
					}}
					currentMaskedEmail={recoveryEmail?.maskedEmail || null}
					mfaMethod={methodMap[settings?.mfa_methods[0].method]}
					primaryEmail={settings?.email}
					onSuccess={handleRecoveryEmailSuccess}
				/>
			)}
		</>
	);
};

MyProfileTab.propTypes = {
	settings: PropTypes.object.isRequired,
	languages: PropTypes.object.isRequired,
	timezones: PropTypes.array.isRequired,
	usesSso: PropTypes.bool.isRequired,
	usesGoogleSSO: PropTypes.bool.isRequired,
	setSettings: PropTypes.func.isRequired,
	reset: PropTypes.bool.isRequired,
	editingFields: PropTypes.object.isRequired,
	setEditingFields: PropTypes.func.isRequired,
	cancelEmailChangeRequest: PropTypes.func.isRequired,
	resendEmailChangeRequest: PropTypes.func.isRequired,
	handleDisconnectGoogleSignOn: PropTypes.func.isRequired,
	setToastInfo: PropTypes.func.isRequired,
	assumedRole: PropTypes.object,
	isSuperuser: PropTypes.bool,
};

MyProfileTab.defaultProps = { assumedRole: {}, isSuperuser: false };

export default MyProfileTab;
